import { useStore } from '@nanostores/react';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { runtime } from '~/lib/runtime';
import { $wakeUpFn, $wakingUp, $resumeFailed } from '~/lib/stores/sandbox';
import { workbenchStore } from '~/lib/stores/workbench';

export function SleepBanner() {
  const status = useStore(runtime.status);
  const wakeUpFn = useStore($wakeUpFn);
  const resumeFailed = useStore($resumeFailed);
  const [waking, setWaking] = useState(false);

  // resumeFailed keeps the banner up after a resume that produced no preview,
  // without writing 'sleeping' into runtime.status — which would stop the
  // runtime healing itself when the pod turns out to be merely slow.
  if (status !== 'sleeping' && !waking && !resumeFailed) {
    return null;
  }

  const handleWakeUp = async () => {
    if (waking) {
      return;
    }

    if (!wakeUpFn) {
      /*
       * Not every path to 'sleeping' carries a wake closure — the session-timeout
       * handler in sandbox-runtime flips the status without one. Early-returning
       * here rendered a button that visibly did nothing, and a reload was the
       * only way out. Do the reload for the user instead of pretending to.
       */
      console.warn('[SleepBanner] No wake closure registered — reloading to recover');
      window.location.reload();

      return;
    }

    setWaking(true);
    $wakingUp.set(true);
    $resumeFailed.set(false);

    // Snapshot what we are about to clear. Clearing shows the loading carousel
    // during the wake, but on failure it must go back — otherwise the static
    // preview the user was looking at is gone for the rest of the session.
    const hadStaticPreview = runtime.hasStaticPreview.get();
    const previousPreviews = workbenchStore.previews.get();

    runtime.hasStaticPreview.set(false);
    workbenchStore.previews.set([]);

    try {
      await wakeUpFn();
      $resumeFailed.set(false);
    } catch (err) {
      // Keep $wakeUpFn — this button is the retry. Clearing it on failure (or
      // on a resume that resolved without ever producing a preview) left the
      // banner up with an inert button.
      console.error('Wake up failed:', err);
      runtime.hasStaticPreview.set(hadStaticPreview);
      workbenchStore.previews.set(previousPreviews);

      // Without this the failure is still silent from the user's seat: the
      // button just flips back from "Resuming…" to "Resume" with no
      // explanation, indistinguishable from a click that did nothing.
      toast.error(err instanceof Error ? err.message : 'Resume failed — please try again.');
    } finally {
      setWaking(false);
      $wakingUp.set(false);
    }
  };

  return (
    <div className="ui-banner-idle w-full">
      <span className="i-ph:moon-stars text-muted text-[14px] flex-shrink-0" />
      <span className="flex-1 min-w-0">Sandbox went to sleep due to inactivity. Your project files are saved.</span>
      <button
        type="button"
        onClick={handleWakeUp}
        disabled={waking}
        className="ui-btn-ghost flex items-center gap-1 disabled:cursor-wait"
      >
        {waking ? (
          <>
            <span className="i-svg-spinners:90-ring-with-bg text-[12px]" />
            Resuming…
          </>
        ) : (
          <>
            <span className="i-ph:power text-[12px]" />
            Resume
          </>
        )}
      </button>
    </div>
  );
}
